import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { X, Check, Crown, CreditCard, Sparkle, Spinner, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fetchTiers, FALLBACK_TIERS } from "../lib/tiers";

const PRO_PERKS = [
  "No watermark on exports",
  "PDF export + live share links",
  "Custom font uploads",
  "Full template marketplace",
  "AI Style Replication Studio",
];

function PayPalArea({ planId, onSuccess }) {
  const [{ isPending }] = usePayPalScriptReducer();
  return (
    <div className="relative min-h-[100px]">
      {isPending && (
        <div className="absolute inset-0 flex items-center justify-center gap-2 text-zinc-500 text-xs" data-testid="paypal-loading">
          <Spinner size={16} className="animate-spin" /> Loading PayPal…
        </div>
      )}
      <PayPalButtons
        style={{ layout: "vertical", color: "gold", shape: "pill", height: 44, tagline: false, label: "pay" }}
        forceReRender={[planId]}
        createOrder={async () => { const r = await api.post("/paypal/create-order", { plan_id: planId }); return r.data.order_id; }}
        onApprove={async (data) => {
          try {
            const r = await api.post("/paypal/capture-order", { order_id: data.orderID });
            if (r.data.status === "COMPLETED") { toast.success("Payment complete — welcome to Pro!"); onSuccess && onSuccess(); }
            else toast.error("Payment was not completed.");
          } catch (e) { toast.error("Could not confirm PayPal payment."); }
        }}
        onError={() => toast.error("PayPal could not process this payment.")}
      />
    </div>
  );
}

export default function UpgradeModal({ open, reason, tiers, paypalClientId, onClose, onSuccess }) {
  const [loadedTiers, setLoadedTiers] = useState(null);
  const list = (tiers && tiers.length) ? tiers : (loadedTiers && loadedTiers.length ? loadedTiers : FALLBACK_TIERS);
  const [billing, setBilling] = useState("monthly");
  const [tierKey, setTierKey] = useState("promoter");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && (!tiers || !tiers.length) && !loadedTiers) {
      fetchTiers().then(setLoadedTiers).catch(() => setLoadedTiers(FALLBACK_TIERS));
    }
  }, [open, tiers, loadedTiers]);

  if (!open) return null;

  const tier = list.find((t) => t.key === tierKey) || list[0];
  const opt = tier[billing];
  const planId = opt.plan_id;
  const monthlyEquiv = billing === "annual" ? (opt.amount / 12).toFixed(2) : opt.amount.toFixed(2);

  const startStripe = async () => {
    setLoading(true);
    try {
      const res = await api.post("/payments/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="upgrade-modal">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-[#101013] border border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 pb-0">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/15 text-amber-300 text-xs font-semibold mb-3">
              <Crown weight="fill" size={13} /> FlyerStudio Pro
            </div>
            <h2 className="font-display text-2xl font-black tracking-tight text-white">Choose your plan</h2>
            {reason ? <p className="text-sm text-amber-300/90 mt-1" data-testid="upgrade-reason">{reason}</p>
              : <p className="text-sm text-zinc-400 mt-1">Pick a tier — monthly resets each month, annual deposits the full year upfront.</p>}
          </div>
          <button onClick={onClose} data-testid="upgrade-close-btn" className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-zinc-400"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Billing toggle */}
          <div className="flex items-center justify-center">
            <div className="inline-flex rounded-full bg-white/5 border border-white/10 p-1">
              <button data-testid="billing-monthly" onClick={() => setBilling("monthly")} className={`px-5 py-2 rounded-full text-sm font-bold transition-colors ${billing === "monthly" ? "bg-white text-black" : "text-zinc-300"}`}>Monthly</button>
              <button data-testid="billing-annual" onClick={() => setBilling("annual")} className={`px-5 py-2 rounded-full text-sm font-bold transition-colors inline-flex items-center gap-1.5 ${billing === "annual" ? "bg-white text-black" : "text-zinc-300"}`}>Annual <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">Save ~17%</span></button>
            </div>
          </div>

          {/* Tier cards */}
          <div className="grid sm:grid-cols-3 gap-3">
            {list.map((t) => {
              const o = t[billing];
              const selected = t.key === tierKey;
              return (
                <button key={t.key} data-testid={`tier-${t.key}`} onClick={() => setTierKey(t.key)}
                  className={`relative text-left p-4 rounded-xl border transition-colors ${selected ? "border-amber-400 bg-amber-400/10" : "border-white/10 hover:border-white/25"}`}>
                  {t.popular && <span className="absolute -top-2.5 right-3 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400 text-black">Popular</span>}
                  <p className="font-display font-bold text-white">{t.name}</p>
                  <p className="text-[11px] text-zinc-500 mb-2 leading-snug min-h-[28px]">{t.blurb}</p>
                  <div className="text-2xl font-black text-white">${billing === "annual" ? (o.amount / 12).toFixed(0) : o.amount}<span className="text-xs font-medium text-zinc-400">/mo</span></div>
                  <div className="text-[11px] text-zinc-500">{billing === "annual" ? `$${o.amount}/yr billed once` : "billed monthly"}</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-300"><Lightning weight="fill" size={12} /> {billing === "annual" ? `${o.credits} credits upfront` : `${o.credits} credits / mo`}</div>
                </button>
              );
            })}
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <ul className="space-y-1.5">
              {PRO_PERKS.map((p) => (
                <li key={p} className="flex items-center gap-2 text-sm text-zinc-300"><Check weight="bold" size={14} className="text-emerald-400 shrink-0" /> {p}</li>
              ))}
            </ul>

            <div className="rounded-xl border border-white/10 p-4 bg-white/[0.02]">
              <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-1">You pay</p>
              <p className="text-lg font-black text-white mb-3" data-testid="upgrade-total">${opt.amount.toFixed(2)} <span className="text-xs font-normal text-zinc-500">{billing === "annual" ? "/year" : "/month"} · ${monthlyEquiv}/mo</span></p>
              <button onClick={startStripe} disabled={loading} data-testid="pay-stripe-btn"
                className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-semibold transition-colors">
                <CreditCard weight="fill" size={18} /> {loading ? "Redirecting…" : "Pay with Card"}
              </button>
              <div className="flex items-center gap-3 my-3"><div className="h-px flex-1 bg-white/10" /><span className="text-[10px] uppercase tracking-wider text-zinc-600">or</span><div className="h-px flex-1 bg-white/10" /></div>
              {paypalClientId ? (
                <div data-testid="paypal-buttons">
                  <PayPalScriptProvider options={{ clientId: paypalClientId, currency: "USD", intent: "capture" }}>
                    <PayPalArea planId={planId} onSuccess={onSuccess} />
                  </PayPalScriptProvider>
                </div>
              ) : <p className="text-xs text-zinc-500 text-center py-2">Loading PayPal…</p>}
            </div>
          </div>
          <p className="text-[11px] text-zinc-500 flex items-start gap-1.5"><Sparkle size={13} className="text-blue-400 mt-0.5 shrink-0" /> Credits are delivered the second your payment confirms. Cancel anytime — access stays active until it expires.</p>
        </div>
      </div>
    </div>
  );
}
